import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useApi } from "~/api/provider";
import { useCollections } from "~/api/queries";
import { Select, TextArea } from "~/features/settings/controls";
import {
  type BoardEntry,
  describeLadder,
  type RoomEvent,
  type ThreadSource,
  type ThreadView,
  type ThreadWrite,
  useJournal,
  useThread,
  useThreadAction,
  useThreads,
} from "~/features/settings/sections/thinkers/threads/threadsApi";
import { Button } from "~/foundation/components/Button";
import { Input } from "~/foundation/components/Input";
import { Spinner } from "~/foundation/components/Spinner";
import { Text } from "~/foundation/components/Text";
import { cn } from "~/foundation/utils/cn";

const SOURCE_KINDS = [
  "work",
  "health",
  "document",
  "mail",
  "canopy",
  "query",
  "url",
  "housekeeping",
  "knowledge",
] as const;
const TRIGGER_KINDS = ["change", "cron", "event", "manual"] as const;

const when = (iso: string | null | undefined) => (iso ? new Date(iso).toLocaleString() : "—");

/** A source row as the form holds it: a key that survives edits and removals. */
type SourceDraft = ThreadSource & { key: number };
let nextSource = 0;

/**
 * BRONCO Opus 03 — thinking threads: the standing interests a thinker keeps thinking about. A tick is a thinkers room
 * over what changed (micro seats first; an open or unsure board climbs to the big seat, then to ONE plain question for
 * you). The list says what each thread did last; a thread's page shows the live board of its current tick, its budget,
 * the ladder it climbed and its journal (the kept boards). Opus 06 moves this into the assistant's stall.
 */
export function ThreadsPanel({
  canAdminister,
  openThreadId,
  onOpen,
}: {
  canAdminister: boolean;
  openThreadId: string | null;
  onOpen: (id: string | null) => void;
}) {
  const { t } = useTranslation();
  const threads = useThreads();
  const [creating, setCreating] = useState(false);

  if (openThreadId) {
    return <ThreadPage id={openThreadId} onBack={() => onOpen(null)} />;
  }
  if (creating) {
    return (
      <NewThread
        onDone={(id) => {
          setCreating(false);
          if (id) onOpen(id);
        }}
      />
    );
  }
  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <Text variant="small">
          {t(
            "A thread is something a thinker keeps thinking about. When what it reads changes, it thinks it over in a room — small thinkers first; an open question climbs to a bigger thinker, then to one plain question for you.",
          )}
        </Text>
        <Button size="sm" variant="primary" onClick={() => setCreating(true)}>
          {t("New thread")}
        </Button>
      </div>
      {threads.isLoading ? <Spinner label={t("Loading")} /> : null}
      {threads.isError ? (
        <p role="alert" className="text-small text-danger">
          {(threads.error as Error).message}
        </p>
      ) : null}
      {threads.data && threads.data.length === 0 ? (
        <Text variant="small">{t("No threads yet.")}</Text>
      ) : null}
      <ul className="flex flex-col gap-2">
        {(threads.data ?? []).map((row) => (
          <li key={row.id}>
            <button
              type="button"
              className="flex w-full flex-col gap-1 rounded border border-divider p-2 text-start hover:border-text/40"
              onClick={() => onOpen(row.id)}
            >
              <span className="flex flex-wrap items-center gap-2">
                <span className="font-medium text-text">{row.title}</span>
                <StatusPill status={row.ticking ? "thinking" : row.status} />
                {row.openQuestionId ? (
                  <span className="rounded bg-warning/15 px-1.5 text-small text-warning">
                    {t("waiting on you")}
                  </span>
                ) : null}
              </span>
              <span className="text-small text-text-secondary">
                {t("Thinker {{owner}} · ticks on {{trigger}}", {
                  owner: row.ownerId,
                  trigger: t(row.trigger.kind),
                })}
                {row.lastTick
                  ? ` · ${t("last: {{outcome}}", { outcome: t(row.lastTick.outcome) })}${row.lastTick.skippedWhy ? ` — ${row.lastTick.skippedWhy}` : ""}${row.lastTick.repeats > 1 ? ` ×${row.lastTick.repeats}` : ""} · ${when(row.lastTick.at)}`
                  : ` · ${t("not ticked yet")}`}
              </span>
              {row.lastTick?.conclusion ? (
                <span className="text-small text-text">{row.lastTick.conclusion}</span>
              ) : null}
            </button>
          </li>
        ))}
      </ul>
      {!canAdminister ? (
        <Text variant="small">{t("You change the threads you made; an admin changes any.")}</Text>
      ) : null}
    </div>
  );
}

function StatusPill({ status }: { status: string }) {
  const { t } = useTranslation();
  const tone =
    status === "active" || status === "settled"
      ? "bg-success/15 text-success"
      : status === "thinking" || status === "running" || status === "open"
        ? "bg-accent/15 text-accent"
        : status === "failed"
          ? "bg-danger/15 text-danger"
          : "bg-bg-tertiary text-text-secondary";
  return <span className={cn("rounded px-1.5 text-small", tone)}>{t(status)}</span>;
}

function Meter({ label, used, of }: { label: string; used: number; of: number }) {
  const share = of <= 0 ? 1 : Math.min(1, used / of);
  return (
    <div className="flex min-w-40 flex-1 flex-col gap-1">
      <span className="text-small text-text-secondary">
        {label}: {used} / {of}
      </span>
      <div className="h-1.5 rounded bg-bg-tertiary" aria-hidden>
        <div
          className={cn("h-1.5 rounded", share >= 1 ? "bg-danger" : "bg-accent")}
          style={{ width: `${share * 100}%` }}
        />
      </div>
    </div>
  );
}

function ThreadPage({ id, onBack }: { id: string; onBack: () => void }) {
  const { t } = useTranslation();
  const thread = useThread(id);
  const journal = useJournal(id);
  const tick = useThreadAction<{ id: string }>("threads.tick");
  const pause = useThreadAction<{ id: string }>("threads.pause");
  const resume = useThreadAction<{ id: string }>("threads.resume");
  const retire = useThreadAction<{ id: string }>("threads.retire");
  const failure = [tick, pause, resume, retire].find((action) => action.isError)?.error as
    | Error
    | undefined;

  if (thread.isLoading) return <Spinner label={t("Loading")} />;
  if (thread.isError || !thread.data) {
    return (
      <div className="flex flex-col gap-2">
        <Button size="sm" variant="neutral" onClick={onBack}>
          {t("All threads")}
        </Button>
        <p role="alert" className="text-small text-danger">
          {(thread.error as Error | null)?.message ?? t("The thread could not be loaded.")}
        </p>
      </div>
    );
  }
  const view = thread.data;
  const latest = view.ticks.find((entry) => entry.roomId);
  const thinking =
    view.ticking || view.room?.status === "running" || Boolean(view.state.tickRequestedAt);

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex min-w-0 flex-col gap-1">
          <Button size="sm" variant="neutral" onClick={onBack} className="self-start">
            {t("All threads")}
          </Button>
          <h3 className="flex flex-wrap items-center gap-2 font-medium">
            {view.title} <StatusPill status={thinking ? "thinking" : view.status} />
          </h3>
          <Text variant="small">{view.interest}</Text>
          <Text variant="small">
            {t("Thinker {{owner}} · reads {{sources}} · ticks on {{trigger}}", {
              owner: view.ownerId,
              sources: view.sources.map((s) => `${t(s.kind)} ${s.ref}`).join(", "),
              trigger:
                view.trigger.kind === "cron" ? `cron ${view.trigger.expr}` : t(view.trigger.kind),
            })}
          </Text>
        </div>
        <div className="flex shrink-0 flex-wrap gap-2">
          <Button
            size="sm"
            variant="primary"
            disabled={thinking || view.status !== "active" || tick.isPending}
            onClick={() => tick.mutate({ id })}
          >
            {thinking ? t("Thinking…") : t("Think now")}
          </Button>
          {view.status === "active" ? (
            <Button size="sm" variant="neutral" onClick={() => pause.mutate({ id })}>
              {t("Pause")}
            </Button>
          ) : view.status === "paused" ? (
            <Button size="sm" variant="neutral" onClick={() => resume.mutate({ id })}>
              {t("Resume")}
            </Button>
          ) : null}
          {view.status !== "retired" ? (
            <Button size="sm" variant="neutral" onClick={() => retire.mutate({ id })}>
              {t("Retire")}
            </Button>
          ) : null}
        </div>
      </div>
      {failure ? (
        <p role="alert" className="text-small text-danger">
          {failure.message}
        </p>
      ) : null}

      <div className="flex flex-wrap gap-4">
        <Meter
          label={t("Ticks today")}
          used={view.spent.ticksToday}
          of={view.budget.maxTicksPerDay}
        />
        <Meter
          label={t("Big calls today")}
          used={view.spent.bigCallsToday}
          of={view.budget.maxBigCallsPerDay}
        />
      </div>

      {view.openQuestion ? (
        <div className="flex flex-col gap-1 rounded border border-warning/40 p-2">
          <span className="text-small font-medium text-warning">{t("Waiting on your answer")}</span>
          <span className="text-text">{view.openQuestion.title}</span>
          <Link
            to="/work/$key"
            params={{ key: view.openQuestion.key }}
            className="text-small text-accent underline"
          >
            {t("Answer it")}
          </Link>
        </div>
      ) : null}
      {view.state.lastBindingProblem ? (
        <p role="alert" className="text-small text-danger">
          {t("A facet could not take its seat: {{why}}", { why: view.state.lastBindingProblem })}
        </p>
      ) : null}

      {latest ? (
        <Text variant="small">
          {t("Last tick ({{at}}): {{ladder}}", {
            at: when(latest.startedAt),
            ladder: describeLadder(latest, t),
          })}
          {latest.skippedWhy ? ` — ${latest.skippedWhy}` : ""}
        </Text>
      ) : null}

      {view.room ? (
        <LiveBoard room={view.room} />
      ) : (
        <Text variant="small">{t("No tick has thought yet.")}</Text>
      )}

      <section className="flex flex-col gap-2">
        <h4 className="font-medium">{t("Journal")}</h4>
        {view.journalDocumentId ? (
          <Link
            to="/documents/$documentId"
            params={{ documentId: view.journalDocumentId }}
            className="text-small text-accent underline"
          >
            {t("Open the thread's document")}
          </Link>
        ) : null}
        <ul className="flex flex-col gap-1">
          {(journal.data?.entries ?? []).map((entry) => (
            <li
              key={entry.tickId}
              className="flex flex-col rounded border border-divider p-2 text-small"
            >
              <span className="flex flex-wrap items-center gap-2">
                <span className="text-text-secondary">{when(entry.at)}</span>
                <StatusPill status={entry.outcome} />
                <span className="text-text-secondary">{describeLadder(entry, t)}</span>
                {entry.document ? (
                  <Link
                    to="/documents/$documentId"
                    params={{ documentId: entry.document.id }}
                    className="text-accent underline"
                  >
                    {t("the kept board")}
                  </Link>
                ) : null}
              </span>
              {entry.conclusion ? <span className="text-text">{entry.conclusion}</span> : null}
              {entry.note ? <span className="text-text-secondary">{entry.note}</span> : null}
            </li>
          ))}
        </ul>
        <TickLog ticks={view.ticks} />
      </section>
    </div>
  );
}

const GROUPS: { kind: string; label: string }[] = [
  { kind: "conclusion", label: "Conclusion" },
  { kind: "ask", label: "Asked of you" },
  { kind: "claim", label: "Claims" },
  { kind: "question", label: "Questions" },
  { kind: "answer", label: "Answers" },
  { kind: "dispute", label: "Disagreements" },
  { kind: "link", label: "Relations" },
];

/** The current tick's board, live: what the room holds now, its certainty, and the trail of its phases. */
function LiveBoard({ room }: { room: NonNullable<ThreadView["room"]> }) {
  const { t } = useTranslation();
  const live = room.board.entries.filter((entry) =>
    ["open", "disputed", "answered"].includes(entry.status),
  );
  return (
    <section
      className="flex flex-col gap-3 rounded border border-divider p-3"
      aria-label={t("The board")}
    >
      <div className="flex flex-wrap items-center gap-3">
        <h4 className="font-medium">{t("The board")}</h4>
        <StatusPill status={room.status} />
        <span className="text-small text-text-secondary">
          {t("{{n}} live entries · certainty {{c}} · {{calls}} model calls", {
            n: room.board.live,
            c: room.board.certainty.toFixed(2),
            calls: room.modelCalls,
          })}
        </span>
        {room.documentId ? (
          <Link
            to="/documents/$documentId"
            params={{ documentId: room.documentId }}
            className="text-small text-accent underline"
          >
            {t("Kept as a document")}
          </Link>
        ) : null}
      </div>
      {GROUPS.map((group) => {
        const entries = live.filter((entry) => entry.kind === group.kind);
        return entries.length === 0 ? null : (
          <div key={group.kind} className="flex flex-col gap-1">
            <span className="text-small font-medium text-text-secondary">{t(group.label)}</span>
            <ul className="flex flex-col gap-1">
              {entries.map((entry) => (
                <BoardLine key={entry.id} entry={entry} />
              ))}
            </ul>
          </div>
        );
      })}
      <Trail events={room.events} />
    </section>
  );
}

function BoardLine({ entry }: { entry: BoardEntry }) {
  const { t } = useTranslation();
  return (
    <li className="text-small">
      <span className="text-text">{entry.text}</span>
      <span className="text-text-tertiary">
        {" "}
        · {entry.author === "prior" ? t("from the last tick") : entry.author}
        {entry.certainty ? ` · ${t(entry.certainty)}` : ""}
        {entry.confirmedBy.length > 0
          ? ` · ${t("confirmed by {{who}}", { who: entry.confirmedBy.join(", ") })}`
          : ""}
        {entry.status === "disputed" ? ` · ${t("disputed")}` : ""}
        {entry.status === "answered" ? ` · ${t("answered")}` : ""}
      </span>
      {entry.options.length > 0 ? (
        <span className="block text-text-secondary">
          {t("Answers: {{options}}", { options: entry.options.join(" · ") })}
        </span>
      ) : null}
    </li>
  );
}

/** How the room thought, in order: each phase that ran (and who sat in it) or was skipped, and why. */
function Trail({ events }: { events: RoomEvent[] }) {
  const { t } = useTranslation();
  const shown = events.filter((event) =>
    [
      "phase-started",
      "phase-skipped",
      "phase-failed",
      "asked",
      "settled",
      "failed",
      "question-asked",
      "remembered",
    ].includes(event.kind),
  );
  if (shown.length === 0) return null;
  return (
    <details>
      <summary className="cursor-pointer text-small text-text-secondary">
        {t("How the room thought")}
      </summary>
      <ol className="mt-1 flex flex-col gap-0.5 text-small text-text-secondary">
        {shown.map((event) => {
          const data = event.data as Record<string, unknown>;
          const seats = Array.isArray(data.seats)
            ? (data.seats as { role: string; thinker: string }[])
            : [];
          return (
            <li key={event.sequence}>
              {event.kind === "phase-started"
                ? t("{{phase}} — {{seats}}", {
                    phase: String(data.phase),
                    seats: seats.map((s) => `${s.role} on ${s.thinker}`).join(", "),
                  })
                : event.kind === "phase-skipped"
                  ? t("{{phase}} skipped — {{why}}", {
                      phase: String(data.phase),
                      why: String(data.why),
                    })
                  : event.kind === "asked"
                    ? t("asked a person: {{question}}", { question: String(data.question) })
                    : event.kind === "question-asked"
                      ? t("the question went out as {{key}}", { key: String(data.key) })
                      : event.kind === "remembered"
                        ? t("remembered a conclusion ({{tag}})", { tag: String(data.tag) })
                        : `${t(event.kind)}${data.error ? ` — ${String(data.error)}` : ""}`}
            </li>
          );
        })}
      </ol>
    </details>
  );
}

function TickLog({ ticks }: { ticks: ThreadView["ticks"] }) {
  const { t } = useTranslation();
  const skips = ticks.filter((entry) => entry.outcome === "skipped");
  if (skips.length === 0) return null;
  return (
    <details>
      <summary className="cursor-pointer text-small text-text-secondary">
        {t("Skipped ticks ({{n}})", { n: skips.length })}
      </summary>
      <ul className="mt-1 flex flex-col gap-0.5 text-small text-text-secondary">
        {skips.map((entry) => (
          <li key={entry.id}>
            {when(entry.endedAt ?? entry.startedAt)} — {entry.skippedWhy}
            {entry.repeats > 1 ? ` ×${entry.repeats}` : ""}
          </li>
        ))}
      </ul>
    </details>
  );
}

/** A new thread: what it thinks toward, the thinker whose facets think, what it reads, when it ticks, what it may spend. */
function NewThread({ onDone }: { onDone: (id: string | null) => void }) {
  const { t } = useTranslation();
  const api = useApi();
  const collections = useCollections();
  const locals = useQuery({
    queryKey: ["thinkers", "local", "for-threads"],
    queryFn: () => api.thinkers.local.list(),
  });
  const create = useThreadAction<ThreadWrite>("threads.create");
  const [title, setTitle] = useState("");
  const [interest, setInterest] = useState("");
  const [ownerId, setOwnerId] = useState("");
  const [collectionId, setCollectionId] = useState("");
  const [sources, setSources] = useState<SourceDraft[]>([
    { key: nextSource++, kind: "work", ref: "" },
  ]);
  const [trigger, setTrigger] = useState<(typeof TRIGGER_KINDS)[number]>("change");
  const [expr, setExpr] = useState("0 * * * *");
  const [poll, setPoll] = useState(5);
  const [events, setEvents] = useState("transitioned");
  const [ticks, setTicks] = useState(24);
  const [micro, setMicro] = useState(6);
  const [big, setBig] = useState(4);
  const [bigSeat, setBigSeat] = useState("gemma4:26b");
  const [project, setProject] = useState("");

  const submit = () =>
    create.mutate(
      {
        title,
        interest,
        ownerId,
        collectionId,
        sources: sources
          .filter((source) => source.ref.trim() || source.kind === "mail")
          .map(({ kind, ref }) => ({ kind, ref })),
        trigger:
          trigger === "cron"
            ? { kind: "cron", expr }
            : trigger === "event"
              ? {
                  kind: "event",
                  events: events
                    .split(",")
                    .map((e) => e.trim())
                    .filter(Boolean),
                  pollMinutes: poll,
                }
              : trigger === "change"
                ? { kind: "change", pollMinutes: poll }
                : { kind: "manual" },
        budget: { maxTicksPerDay: ticks, maxMicroThoughtsPerTick: micro, maxBigCallsPerDay: big },
        ...(bigSeat.trim() ? { big: { thinkerId: bigSeat.trim() } } : {}),
        ...(project.trim() ? { project: project.trim() } : {}),
      },
      { onSuccess: (data) => onDone((data as { id?: string })?.id ?? null) },
    );

  return (
    <div className="flex flex-col gap-3">
      <h3 className="font-medium">{t("New thread")}</h3>
      <div className="grid gap-3 md:grid-cols-2">
        <Input
          label={t("Title")}
          value={title}
          fullWidth
          placeholder={t("e.g. PLAT health")}
          onChange={(e) => setTitle(e.target.value)}
        />
        <Select
          label={t("Thinker whose facets think")}
          value={ownerId}
          fullWidth
          onChange={(e) => setOwnerId(e.target.value)}
        >
          <option value="">{t("Choose a thinker…")}</option>
          {(locals.data ?? []).map((local) => (
            <option key={local.id} value={local.thinkerId}>
              {local.displayName} ({local.thinkerId})
            </option>
          ))}
        </Select>
        <div className="md:col-span-2">
          <TextArea
            label={t("What it keeps thinking toward")}
            value={interest}
            rows={2}
            onChange={(e) => setInterest(e.target.value)}
          />
        </div>
        <Select
          label={t("Keep its journal in")}
          value={collectionId}
          fullWidth
          onChange={(e) => setCollectionId(e.target.value)}
        >
          <option value="">{t("Choose a collection…")}</option>
          {(collections.data ?? []).map((collection) => (
            <option key={collection.id} value={collection.id}>
              {collection.name}
            </option>
          ))}
        </Select>
        <Input
          label={t("Project (key)")}
          value={project}
          fullWidth
          placeholder="PLAT"
          onChange={(e) => setProject(e.target.value)}
        />
      </div>

      <fieldset className="flex flex-col gap-2">
        <legend className="text-small font-medium text-text">{t("What it reads")}</legend>
        {sources.map((source, index) => (
          <div key={source.key} className="flex flex-wrap items-end gap-2">
            <Select
              label={t("Source")}
              value={source.kind}
              onChange={(e) =>
                setSources(
                  sources.map((s, i) => (i === index ? { ...s, kind: e.target.value } : s)),
                )
              }
            >
              {SOURCE_KINDS.map((kind) => (
                <option key={kind} value={kind}>
                  {t(kind)}
                </option>
              ))}
            </Select>
            <Input
              label={t("Which")}
              value={source.ref}
              placeholder={
                source.kind === "work" || source.kind === "health"
                  ? "PLAT"
                  : source.kind === "url"
                    ? "https://…"
                    : ""
              }
              onChange={(e) =>
                setSources(sources.map((s, i) => (i === index ? { ...s, ref: e.target.value } : s)))
              }
            />
            <Button
              size="sm"
              variant="neutral"
              onClick={() => setSources(sources.filter((_, i) => i !== index))}
            >
              {t("Remove")}
            </Button>
          </div>
        ))}
        <Button
          size="sm"
          variant="neutral"
          className="self-start"
          disabled={sources.length >= 8}
          onClick={() => setSources([...sources, { key: nextSource++, kind: "health", ref: "" }])}
        >
          {t("Add a source")}
        </Button>
      </fieldset>

      <div className="grid gap-3 md:grid-cols-3">
        <Select
          label={t("Ticks when")}
          value={trigger}
          onChange={(e) => setTrigger(e.target.value as (typeof TRIGGER_KINDS)[number])}
        >
          {TRIGGER_KINDS.map((kind) => (
            <option key={kind} value={kind}>
              {t(kind)}
            </option>
          ))}
        </Select>
        {trigger === "cron" ? (
          <Input label={t("Cron (UTC)")} value={expr} onChange={(e) => setExpr(e.target.value)} />
        ) : null}
        {trigger === "change" || trigger === "event" ? (
          <Input
            label={t("Look every (minutes)")}
            type="number"
            value={String(poll)}
            onChange={(e) => setPoll(Number(e.target.value) || 5)}
          />
        ) : null}
        {trigger === "event" ? (
          <Input
            label={t("Work events")}
            value={events}
            onChange={(e) => setEvents(e.target.value)}
          />
        ) : null}
      </div>

      <div className="grid gap-3 md:grid-cols-4">
        <Input
          label={t("Ticks a day")}
          type="number"
          value={String(ticks)}
          onChange={(e) => setTicks(Number(e.target.value) || 1)}
        />
        <Input
          label={t("Micro thoughts a tick")}
          type="number"
          value={String(micro)}
          onChange={(e) => setMicro(Number(e.target.value) || 1)}
        />
        <Input
          label={t("Big calls a day")}
          type="number"
          value={String(big)}
          onChange={(e) => setBig(Number(e.target.value) || 0)}
        />
        <Input
          label={t("Big seat")}
          value={bigSeat}
          placeholder="gemma4:26b"
          onChange={(e) => setBigSeat(e.target.value)}
        />
      </div>

      {create.isError ? (
        <p role="alert" className="text-small text-danger">
          {(create.error as Error).message}
        </p>
      ) : null}
      <div className="flex gap-2">
        <Button
          size="sm"
          variant="primary"
          disabled={
            create.isPending || !title.trim() || !interest.trim() || !ownerId || !collectionId
          }
          onClick={submit}
        >
          {t("Create thread")}
        </Button>
        <Button size="sm" variant="neutral" onClick={() => onDone(null)}>
          {t("Cancel")}
        </Button>
      </div>
    </div>
  );
}
